const SYNC_QUEUE_KEY = 'rq:pilotSyncQueue';
let supabaseGetterPromise = null;

const pilotContext = {
  childName: null,
  sessionId: null,
};

function getQueue() {
  try {
    return JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

function addToQueue(table, data) {
  const queue = getQueue();
  queue.push({ table, data, queuedAt: new Date().toISOString() });
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  } catch {}
}

function clearQueue() {
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, '[]');
  } catch {}
}

export function setPilotSyncContext({ childName = null, sessionId = null } = {}) {
  if (childName !== undefined) pilotContext.childName = childName;
  if (sessionId !== undefined) pilotContext.sessionId = sessionId;
}

export function getPilotSyncContext() {
  return { ...pilotContext };
}

async function getSupabase() {
  if (!supabaseGetterPromise) {
    supabaseGetterPromise = import('./supabase.js').then((mod) => mod.getSupabase);
  }
  const getter = await supabaseGetterPromise;
  return getter();
}

export async function flushSyncQueue() {
  const sb = await getSupabase();
  if (!sb) return;

  const queue = getQueue();
  if (queue.length === 0) return;

  const { data: authData } = await sb.auth.getUser();
  const userId = authData?.user?.id;
  if (!userId) return;

  const failed = [];
  for (const item of queue) {
    const payload = item.data?.user_id ? item.data : { ...item.data, user_id: userId };
    const { error } = await sb.from(item.table).insert(payload);
    if (error) {
      failed.push({ ...item, data: payload });
    }
  }

  if (failed.length > 0) {
    try {
      localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(failed));
    } catch {}
  } else {
    clearQueue();
  }
}

async function syncToPilot(table, data) {
  try {
    const sb = await getSupabase();
    if (!sb) {
      addToQueue(table, data);
      return null;
    }

    const { data: authData } = await sb.auth.getUser();
    const userId = authData?.user?.id;
    if (!userId) {
      addToQueue(table, data);
      return null;
    }

    const payload = { ...data, user_id: userId };
    const { data: result, error } = await sb.from(table).insert(payload).select().single();
    if (error) {
      console.warn(`Pilot sync failed for ${table}, queuing:`, error.message);
      addToQueue(table, payload);
      return null;
    }

    return result;
  } catch (err) {
    console.warn(`Pilot sync failed for ${table}, queuing:`, err?.message || err);
    addToQueue(table, data);
    return null;
  }
}

export async function syncSession({
  childName,
  mode,
  durationSeconds,
  wordsAttempted,
  wordsCorrect,
  score,
  qualityScore,
  guessingTriggers,
  patternTriggers,
  chillModeActivated,
  playlistId,
  levelAtSession
}) {
  const result = await syncToPilot('pilot_sessions', {
    child_name: childName,
    session_date: new Date().toISOString(),
    mode,
    duration_seconds: durationSeconds,
    words_attempted: wordsAttempted || 0,
    words_correct: wordsCorrect || 0,
    score: score || 0,
    quality_score: qualityScore ?? null,
    guessing_triggers: guessingTriggers || 0,
    pattern_triggers: patternTriggers || 0,
    chill_mode_activated: chillModeActivated || false,
    playlist_id: playlistId || null,
    level_at_session: levelAtSession
  });

  if (childName || result?.id) {
    setPilotSyncContext({ childName: childName || null, sessionId: result?.id || null });
  }

  return result?.id || null;
}

export async function syncBKTSnapshot({ childName, sessionId, skillStates }) {
  await syncToPilot('pilot_bkt_snapshots', {
    child_name: childName,
    session_id: sessionId,
    snapshot_date: new Date().toISOString(),
    skill_states: skillStates
  });
}

export async function syncLeitnerEvent({ childName, sessionId, word, correct, oldBox, newBox }) {
  const ctx = getPilotSyncContext();
  const finalChildName = childName || ctx.childName;
  const finalSessionId = sessionId ?? ctx.sessionId ?? null;
  if (!finalChildName) return;

  await syncToPilot('pilot_leitner_events', {
    child_name: finalChildName,
    session_id: finalSessionId,
    word,
    correct,
    old_box: oldBox,
    new_box: newBox,
    event_date: new Date().toISOString()
  });
}

export async function syncAssessment({ childName, assessmentType, levelResult, rawSkillScores, durationSeconds }) {
  await syncToPilot('pilot_assessments', {
    child_name: childName,
    assessment_type: assessmentType,
    assessment_date: new Date().toISOString(),
    level_result: levelResult,
    raw_skill_scores: rawSkillScores || null,
    duration_seconds: durationSeconds || null
  });
}
